import {HTMLAttributes, ReactNode, useContext} from "react";
import { Controller, useFormContext } from "react-hook-form";
import {ProductFormContext} from "@/lib/product-form-context";
import { CheckedButton } from "./CheckedButton";

export const AcceptAction = ({
  type,
  quoteIds, 
  children,
  decisionIndex,
  ...props
} : {
  type: 'checkbox' | 'radio' | 'button';
  quoteIds: string[];
  decisionIndex?: number;
  children?: Readonly<ReactNode>;
} & HTMLAttributes<HTMLDivElement>) => {
  const form = useFormContext();
  const additionalStyles = props.className || 'border rounded';
  const productFormContext = useContext(ProductFormContext);
  const radioGroupName = decisionIndex !== undefined ? 
    `radio-group-${productFormContext}-${decisionIndex}` : 
    `radio-group-${productFormContext}`;

  return (
    <Controller control={form.control} name={`products[${productFormContext}]`} render={({field}) => {
      const isChecked = quoteIds.some(id => field.value?.accepted_quotes.includes(id));
      
      const handleChange = (checked: boolean) => {
        let values = field.value?.accepted_quotes || [];
        
        if (checked) {
          if (type === 'radio' || type === 'button') {
            // For radio/button, only keep quotes from other decisions
            if (decisionIndex !== undefined) {
              values = values.filter((q: string) => !document.querySelector(`[name="${radioGroupName}"][value="${q}"]`));
            } else {
              values = [];
            }
            values.push(...quoteIds);
          } else {
            values.push(...quoteIds.filter(q => !values.includes(q)));
          }
        } else {
          values = values.filter((q: string) => !quoteIds.includes(q));
        }

        field.onChange({
          offer_declined: false,
          accepted_quotes: values,
          available_quotes: field.value?.available_quotes,
          selection_required: field.value?.selection_required
        });
        form.trigger(`products[${productFormContext}]`);
      };

      if (type === 'button') {
        return (
          <div className={`accept-label flex w-full gap-2 text-sm p-2 ${additionalStyles}`}>
            <CheckedButton
              checked={isChecked}
              onChange={(checked) => handleChange(checked)}
              data-group={radioGroupName}
              data-value={quoteIds.join(',')}
            >
              {children}
            </CheckedButton>
          </div>
        );
      }

      return (
        <label className={`accept-label flex w-full gap-2 cursor-pointer text-sm p-2 ${additionalStyles} ${type === 'checkbox' ? 'sm:items-center' : ''} `}>
          <input
            aria-checked={isChecked}
            checked={isChecked}
            value={quoteIds.join(',')}
            type={type}
            role={type}
            name={type === 'radio' ? radioGroupName : undefined}
            onChange={(e) => handleChange(e.target.checked)}
          />
          {children}
        </label>
      );
    }}/>
  )
}
